import { supabase } from '../lib/supabase';
import { createRetryableFunction, handleSupabaseError } from './errorHandler';

// ===================================================================
// AUTHENTICATION SERVICES
// ===================================================================

export const authService = {
  // Sign up with role-based metadata
  async signUp(email, password, userData) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData?.fullName,
            role: userData?.role,
            organization: userData?.organization
          }
        }
      });
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Sign in with retry mechanism
  signIn: createRetryableFunction(async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    return { data, error };
  }),

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      return { error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get current session with retry
  getSession: createRetryableFunction(async () => {
    const { data, error } = await supabase?.auth?.getSession();
    return { data, error };
  }),

  // Reset password
  async resetPassword(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email);
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// USER PROFILE SERVICES
// ===================================================================

export const userProfileService = {
  // Get user profile with retry
  getUserProfile: createRetryableFunction(async (userId) => {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', userId)
      ?.single();
    return { data, error };
  }),

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', userId)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get public profiles for directory with retry
  getPublicProfiles: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('user_profiles')
      ?.select('id, full_name, role, organization, title, location, bio, profile_image_url, skills, created_at');

    if (filters?.role) {
      query = query?.eq('role', filters?.role);
    }
    if (filters?.organization) {
      query = query?.ilike('organization', `%${filters?.organization}%`);
    }
    if (filters?.location) {
      query = query?.ilike('location', `%${filters?.location}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  })
};

// ===================================================================
// BUSINESS DIRECTORY SERVICES
// ===================================================================

export const businessService = {
  // Get all businesses (public access) with retry
  getAllBusinesses: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('businesses')
      ?.select(`
        *,
        owner:user_profiles!owner_id (
          full_name,
          title,
          profile_image_url
        )
      `);

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.city) {
      query = query?.ilike('city', `%${filters?.city}%`);
    }
    if (filters?.search) {
      query = query?.or(`name.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }
    if (filters?.featured) {
      query = query?.eq('is_featured', true);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get single business with retry
  getBusiness: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('businesses')
      ?.select(`
        *,
        owner:user_profiles!owner_id (
          full_name,
          title,
          email,
          linkedin_url,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();
    
    // Increment view count (don't retry this part)
    if (data && !error) {
      try {
        await supabase
          ?.from('businesses')
          ?.update({ view_count: data?.view_count + 1 })
          ?.eq('id', id);
      } catch (viewError) {
        console.warn('Failed to increment view count:', viewError);
      }
    }

    return { data, error };
  }),

  // Create business (authenticated)
  async createBusiness(businessData) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.insert([businessData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Update business (owner only)
  async updateBusiness(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('businesses')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Delete business (owner only)
  async deleteBusiness(id) {
    try {
      const { error } = await supabase
        ?.from('businesses')
        ?.delete()
        ?.eq('id', id);
      return { error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// EVENTS SERVICES
// ===================================================================

export const eventService = {
  // Get all events (public access) with retry
  getAllEvents: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('events')
      ?.select(`
        *,
        organizer:user_profiles!organizer_id (
          full_name,
          organization,
          profile_image_url
        )
      `);

    if (filters?.event_type) {
      query = query?.eq('event_type', filters?.event_type);
    }
    if (filters?.upcoming) {
      query = query?.gte('start_date', new Date()?.toISOString());
    }
    if (filters?.featured) {
      query = query?.eq('is_featured', true);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('start_date', { ascending: true });
    return { data, error };
  }),

  // Get single event with retry
  getEvent: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('events')
      ?.select(`
        *,
        organizer:user_profiles!organizer_id (
          full_name,
          organization,
          email,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();
    return { data, error };
  }),

  // Register for event
  async registerForEvent(eventId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('event_registrations')
        ?.insert([{ event_id: eventId, user_id: userId }])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Cancel event registration
  async cancelEventRegistration(eventId, userId) {
    try {
      const { error } = await supabase
        ?.from('event_registrations')
        ?.delete()
        ?.eq('event_id', eventId)
        ?.eq('user_id', userId);
      return { error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get user's event registrations with retry
  getUserEventRegistrations: createRetryableFunction(async (userId) => {
    const { data, error } = await supabase
      ?.from('event_registrations')
      ?.select(`
        *,
        event:events (
          id,
          title,
          start_date,
          end_date,
          location,
          event_type,
          image_url
        )
      `)
      ?.eq('user_id', userId)
      ?.order('registered_at', { ascending: false });
    return { data, error };
  }),

  // Create event (authenticated users)
  async createEvent(eventData) {
    try {
      const { data, error } = await supabase
        ?.from('events')
        ?.insert([eventData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// GRANT OPPORTUNITIES SERVICES
// ===================================================================

export const grantService = {
  // Get all grants (public access) with retry
  getAllGrants: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('grant_opportunities')
      ?.select(`
        *,
        creator:user_profiles!created_by (
          full_name,
          organization
        )
      `);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.focus_area) {
      query = query?.contains('focus_areas', [filters?.focus_area]);
    }
    if (filters?.amount_min) {
      query = query?.gte('amount_max', filters?.amount_min);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('deadline', { ascending: true });
    return { data, error };
  }),

  // Get single grant with retry
  getGrant: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('grant_opportunities')
      ?.select(`
        *,
        creator:user_profiles!created_by (
          full_name,
          organization,
          email
        )
      `)
      ?.eq('id', id)
      ?.single();
    
    // Increment view count (don't retry this part)
    if (data && !error) {
      try {
        await supabase
          ?.from('grant_opportunities')
          ?.update({ view_count: data?.view_count + 1 })
          ?.eq('id', id);
      } catch (viewError) {
        console.warn('Failed to increment view count:', viewError);
      }
    }

    return { data, error };
  }),

  // Create grant opportunity
  async createGrant(grantData) {
    try {
      const { data, error } = await supabase
        ?.from('grant_opportunities')
        ?.insert([grantData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// COURSES SERVICES
// ===================================================================

export const courseService = {
  // Get all published courses (public access)
  getAllCourses: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('courses')
      ?.select(`
        *,
        instructor:user_profiles!instructor_id (
          full_name,
          title,
          organization,
          profile_image_url
        )
      `)
      ?.eq('is_published', true);

    if (filters?.level) {
      query = query?.eq('level', filters?.level);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get single course
  getCourse: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('courses')
      ?.select(`
        *,
        instructor:user_profiles!instructor_id (
          full_name,
          title,
          organization,
          bio,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();
    return { data, error };
  }),

  // Enroll in course
  async enrollInCourse(courseId, studentId) {
    try {
      const { data, error } = await supabase
        ?.from('course_enrollments')
        ?.insert([{ course_id: courseId, student_id: studentId }])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get user's course enrollments
  getUserCourseEnrollments: createRetryableFunction(async (userId) => {
    const { data, error } = await supabase
      ?.from('course_enrollments')
      ?.select(`
        *,
        course:courses (
          id,
          title,
          duration_hours,
          image_url,
          instructor:user_profiles!instructor_id (
            full_name
          )
        )
      `)
      ?.eq('student_id', userId)
      ?.order('enrolled_at', { ascending: false });
    return { data, error };
  })
};

// ===================================================================
// PRODUCTS SERVICES
// ===================================================================

export const productService = {
  // Get all products (public access)
  getAllProducts: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('products')
      ?.select(`
        *,
        creator:user_profiles!creator_id (
          full_name,
          organization,
          profile_image_url
        )
      `);

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.search) {
      query = query?.or(`name.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get single product
  getProduct: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('products')
      ?.select(`
        *,
        creator:user_profiles!creator_id (
          full_name,
          title,
          organization,
          email,
          linkedin_url,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();
    
    // Increment view count
    if (data && !error) {
      try {
        await supabase
          ?.from('products')
          ?.update({ view_count: data?.view_count + 1 })
          ?.eq('id', id);
      } catch (viewError) {
        console.warn('Failed to increment view count:', viewError);
      }
    }

    return { data, error };
  }),

  // Create product
  async createProduct(productData) {
    try {
      const { data, error } = await supabase
        ?.from('products')
        ?.insert([productData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// FORUM SERVICES
// ===================================================================

export const forumService = {
  // Get all forum posts (public access)
  getAllForumPosts: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('forum_posts')
      ?.select(`
        *,
        author:user_profiles!author_id (
          full_name,
          role,
          organization,
          profile_image_url
        )
      `);

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.post_type) {
      query = query?.eq('post_type', filters?.post_type);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get single forum post with replies
  getForumPost: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('forum_posts')
      ?.select(`
        *,
        author:user_profiles!author_id (
          full_name,
          role,
          organization,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();

    // Get replies separately
    if (data && !error) {
      try {
        const { data: replies, error: repliesError } = await supabase
          ?.from('forum_replies')
          ?.select(`
            *,
            author:user_profiles!author_id (
              full_name,
              role,
              organization,
              profile_image_url
            )
          `)
          ?.eq('post_id', id)
          ?.order('created_at', { ascending: true });

        if (!repliesError) {
          data.replies = replies;
        }

        // Increment view count
        await supabase
          ?.from('forum_posts')
          ?.update({ view_count: data?.view_count + 1 })
          ?.eq('id', id);
      } catch (viewError) {
        console.warn('Failed to load replies or increment view count:', viewError);
      }
    }

    return { data, error };
  }),

  // Create forum post
  async createForumPost(postData) {
    try {
      const { data, error } = await supabase
        ?.from('forum_posts')
        ?.insert([postData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Create forum reply
  async createForumReply(replyData) {
    try {
      const { data, error } = await supabase
        ?.from('forum_replies')
        ?.insert([replyData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// EMAIL AND NOTIFICATIONS SERVICES
// ===================================================================

export const emailService = {
  // Send email via edge function
  async sendEmail(emailData) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: emailData
      });
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Subscribe to email notifications
  async updateEmailSubscription(userId, subscriptionType, isSubscribed) {
    try {
      const { data, error } = await supabase
        ?.from('email_subscriptions')
        ?.upsert([{
          user_id: userId,
          subscription_type: subscriptionType,
          is_subscribed: isSubscribed
        }])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get user's email subscriptions
  getUserEmailSubscriptions: createRetryableFunction(async (userId) => {
    const { data, error } = await supabase
      ?.from('email_subscriptions')
      ?.select('*')
      ?.eq('user_id', userId);
    return { data, error };
  })
};

export const notificationService = {
  // Get user notifications
  getUserNotifications: createRetryableFunction(async (userId, limit = 20) => {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);
    return { data, error };
  }),

  // Mark notification as read
  async markNotificationRead(notificationId) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('id', notificationId)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Create notification
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.insert([notificationData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

// ===================================================================
// FILE STORAGE SERVICES
// ===================================================================

export const storageService = {
  // Upload file to public bucket
  async uploadPublicFile(bucketName, filePath, file) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucketName)
        ?.upload(filePath, file, { upsert: true });
      
      if (error) return { data: null, error };
      
      const { data: publicData } = supabase?.storage
        ?.from(bucketName)
        ?.getPublicUrl(filePath);
      
      return { data: { ...data, publicUrl: publicData?.publicUrl }, error: null };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Upload file to private bucket
  async uploadPrivateFile(bucketName, filePath, file) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucketName)
        ?.upload(filePath, file, { upsert: true });
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get signed URL for private file
  async getSignedUrl(bucketName, filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucketName)
        ?.createSignedUrl(filePath, expiresIn);
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Delete file
  async deleteFile(bucketName, filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucketName)
        ?.remove([filePath]);
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // List files in folder
  listFiles: createRetryableFunction(async (bucketName, folderPath) => {
    const { data, error } = await supabase?.storage
      ?.from(bucketName)
      ?.list(folderPath);
    return { data, error };
  })
};

// ===================================================================
// AI KNOWLEDGE BASE SERVICES
// ===================================================================

export const knowledgeBaseService = {
  // Get all knowledge categories (public access) with retry
  getKnowledgeCategories: createRetryableFunction(async () => {
    const { data, error } = await supabase
      ?.from('knowledge_categories')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('sort_order', { ascending: true });
    return { data, error };
  }),

  // Create knowledge category (admin only)
  async createKnowledgeCategory(categoryData) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_categories')
        ?.insert([categoryData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Update knowledge category (admin only)
  async updateKnowledgeCategory(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_categories')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get all knowledge base articles (admin view - includes pending approval)
  getAllKnowledgeBase: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('knowledge_base')
      ?.select(`
        *,
        category:knowledge_categories (
          name,
          description,
          icon,
          color
        ),
        author:user_profiles!author_id (
          full_name,
          organization
        )
      `);

    if (filters?.category_id) {
      query = query?.eq('category_id', filters?.category_id);
    }
    if (filters?.is_approved !== undefined) {
      query = query?.eq('is_approved', filters?.is_approved);
    }
    if (filters?.is_public !== undefined) {
      query = query?.eq('is_public', filters?.is_public);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get approved knowledge base articles (public access)
  getPublicKnowledgeBase: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('knowledge_base')
      ?.select(`
        *,
        category:knowledge_categories (
          name,
          description,
          icon,
          color
        ),
        author:user_profiles!author_id (
          full_name,
          organization
        )
      `)
      ?.eq('is_public', true)
      ?.eq('is_approved', true);

    if (filters?.category_id) {
      query = query?.eq('category_id', filters?.category_id);
    }
    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Get single knowledge base article
  getKnowledgeBaseArticle: createRetryableFunction(async (id) => {
    const { data, error } = await supabase
      ?.from('knowledge_base')
      ?.select(`
        *,
        category:knowledge_categories (
          name,
          description,
          icon,
          color
        ),
        author:user_profiles!author_id (
          full_name,
          title,
          organization,
          profile_image_url
        )
      `)
      ?.eq('id', id)
      ?.single();
    
    // Increment view count (don't retry this part)
    if (data && !error) {
      try {
        await supabase
          ?.from('knowledge_base')
          ?.update({ view_count: data?.view_count + 1 })
          ?.eq('id', id);
      } catch (viewError) {
        console.warn('Failed to increment view count:', viewError);
      }
    }

    return { data, error };
  }),

  // Create knowledge base article
  async createKnowledgeBaseArticle(articleData) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base')
        ?.insert([articleData])
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Update knowledge base article
  async updateKnowledgeBaseArticle(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Approve knowledge base article (admin only)
  async approveKnowledgeBaseArticle(id) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base')
        ?.update({ 
          is_approved: true, 
          updated_at: new Date()?.toISOString() 
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Reject knowledge base article (admin only)
  async rejectKnowledgeBaseArticle(id) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base')
        ?.update({ 
          is_approved: false,
          updated_at: new Date()?.toISOString() 
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Delete knowledge base article (admin or author)
  async deleteKnowledgeBaseArticle(id) {
    try {
      const { error } = await supabase
        ?.from('knowledge_base')
        ?.delete()
        ?.eq('id', id);
      return { error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Upload knowledge base document with metadata extraction
  async uploadKnowledgeDocument(file, metadata) {
    try {
      const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
      const fileName = `${Date.now()}-${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `knowledge-base/${fileName}`;

      // Upload file to private storage bucket
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('knowledge-documents')
        ?.upload(filePath, file);

      if (uploadError) {
        return { data: null, error: uploadError };
      }

      // Create knowledge base entry with file reference
      const articleData = {
        title: metadata?.title || file?.name?.replace(/\.[^/.]+$/, ''),
        content: metadata?.content || 'Document uploaded for AI processing',
        summary: metadata?.summary || `${file?.name} - ${Math.round(file?.size / 1024)} KB`,
        category_id: metadata?.category_id,
        author_id: metadata?.author_id,
        document_type: fileExtension || 'document',
        source_url: filePath,
        tags: metadata?.tags || [],
        keywords: metadata?.keywords || [],
        is_public: metadata?.is_public !== false,
        is_approved: false // Admin needs to approve uploaded documents
      };

      const { data: articleResult, error: articleError } = await this.createKnowledgeBaseArticle(articleData);

      if (articleError) {
        // Clean up uploaded file if article creation fails
        await supabase?.storage?.from('knowledge-documents')?.remove([filePath]);
        return { data: null, error: articleError };
      }

      return { 
        data: { 
          article: articleResult, 
          filePath,
          fileUrl: uploadData?.path
        }, 
        error: null 
      };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Get file content for processing (admin only)
  async getKnowledgeDocument(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('knowledge-documents')
        ?.download(filePath);
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Search knowledge base with vector similarity (if embeddings exist)
  searchKnowledgeBase: createRetryableFunction(async (query, limit = 10) => {
    const { data, error } = await supabase
      ?.from('knowledge_base')
      ?.select(`
        *,
        category:knowledge_categories (
          name,
          icon,
          color
        )
      `)
      ?.eq('is_public', true)
      ?.eq('is_approved', true)
      ?.or(`title.ilike.%${query}%,content.ilike.%${query}%,summary.ilike.%${query}%`)
      ?.limit(limit);
    
    return { data, error };
  })
};

// ===================================================================
// REAL-TIME SUBSCRIPTIONS
// ===================================================================

export const realtimeService = {
  // Subscribe to table changes
  subscribeToTable(tableName, callback) {
    const channel = supabase
      ?.channel(`${tableName}-changes`)
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        callback
      )
      ?.subscribe();
    
    return channel;
  },

  // Subscribe to specific record changes
  subscribeToRecord(tableName, recordId, callback) {
    const channel = supabase
      ?.channel(`${tableName}-${recordId}`)
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: tableName, filter: `id=eq.${recordId}` },
        callback
      )
      ?.subscribe();
    
    return channel;
  },

  // Unsubscribe from channel
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

// ===================================================================
// ROLE AUTHORIZATION SERVICES
// ===================================================================

export const roleAuthorizationService = {
  // Check if email is authorized for a specific role
  async checkEmailAuthorization(email, role) {
    try {
      const { data, error } = await supabase?.rpc('is_email_authorized_for_role', {
        check_email: email,
        check_role: role
      });
      
      if (error) {
        console.error('Email authorization check failed:', error);
        return { authorized: false, error };
      }
      
      return { authorized: data || false, error: null };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { authorized: false, error: { message: errorInfo?.message } };
    }
  },

  // Get authorized role for an email
  async getAuthorizedRoleForEmail(email) {
    try {
      const { data, error } = await supabase?.rpc('get_authorized_role_for_email', {
        check_email: email
      });
      
      if (error) {
        console.error('Get authorized role failed:', error);
        return { role: null, error };
      }
      
      return { role: data, error: null };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { role: null, error: { message: errorInfo?.message } };
    }
  },

  // Get all role authorizations (admin only)
  getAllRoleAuthorizations: createRetryableFunction(async (filters = {}) => {
    let query = supabase
      ?.from('role_email_authorizations')
      ?.select(`
        *,
        authorized_by_user:user_profiles!authorized_by (
          full_name,
          email
        )
      `)
      ?.eq('is_active', true);

    if (filters?.role) {
      query = query?.eq('role', filters?.role);
    }
    if (filters?.email) {
      query = query?.ilike('email', `%${filters?.email}%`);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  }),

  // Add email authorization (admin only)
  async addEmailAuthorization(email, role, notes = null) {
    try {
      const { data, error } = await supabase
        ?.from('role_email_authorizations')
        ?.insert([{
          email: email?.toLowerCase()?.trim(),
          role,
          notes,
          is_active: true
        }])
        ?.select()
        ?.single();
      
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  },

  // Remove email authorization (admin only)
  async removeEmailAuthorization(id) {
    try {
      const { data, error } = await supabase
        ?.from('role_email_authorizations')
        ?.update({ is_active: false })
        ?.eq('id', id)
        ?.select()
        ?.single();
      
      return { data, error };
    } catch (error) {
      const errorInfo = handleSupabaseError(error);
      return { error: { message: errorInfo?.message } };
    }
  }
};

export default {
  auth: authService,
  userProfile: userProfileService,
  roleAuthorization: roleAuthorizationService,
  business: businessService,
  event: eventService,
  grant: grantService,
  course: courseService,
  product: productService,
  forum: forumService,
  email: emailService,
  notification: notificationService,
  storage: storageService,
  knowledgeBase: knowledgeBaseService,
  realtime: realtimeService
};